// What the side list and several views share: address books, labels, households and the number of
// duplicate suggestions. One small context; views reload it after they change something.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from './api';
import type { Book, Household, LabelCount, Me, Person, PersonForm } from './types';

interface Data {
  me: Me;
  books: Book[];
  labels: LabelCount[];
  households: Household[];
  duplicates: number;
  /** Bumped after every change, so open lists refetch. */
  version: number;
  reload(): Promise<void>;
}

const Ctx = createContext<Data | null>(null);

export function DataProvider({ me, children }: { me: Me; children: ReactNode }) {
  const [books, setBooks] = useState<Book[]>([]);
  const [labels, setLabels] = useState<LabelCount[]>([]);
  const [households, setHouseholds] = useState<Household[]>([]);
  const [duplicates, setDuplicates] = useState(0);
  const [version, setVersion] = useState(0);
  const reload = useCallback(async () => {
    const [b, l, h, d] = await Promise.all([
      api<{ books: Book[] }>('/api/books'),
      api<{ labels: LabelCount[] }>('/api/labels'),
      api<{ households: Household[] }>('/api/households'),
      api<{ suggestions: unknown[] }>('/api/duplicates'),
    ]);
    setBooks(b.books); setLabels(l.labels); setHouseholds(h.households); setDuplicates(d.suggestions.length);
    setVersion((v) => v + 1);
  }, []);
  useEffect(() => { reload().catch(() => {}); }, [reload]);
  const value = useMemo(() => ({ me, books, labels, households, duplicates, version, reload }), [me, books, labels, households, duplicates, version, reload]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useData(): Data {
  const d = useContext(Ctx);
  if (!d) throw new Error('useData outside DataProvider');
  return d;
}

/** Books this person may add people to. */
export const writableBooks = (books: Book[]) => books.filter((b) => b.role !== 'view');

/** Languages that put the family name first ("Kovács Anna", "山田 太郎"; Contacts PRD "Now": country-specific name
 *  order). The reader's language decides, not the person's: everyone sees names the way they are used to. */
const FAMILY_FIRST = new Set(['hu', 'ja', 'zh', 'ko', 'vi', 'mn']);

/** Whether this reader writes the family name first (the browser's languages). */
export function familyFirst(langs: readonly string[] = typeof navigator !== 'undefined' ? navigator.languages : []): boolean {
  const lang = (langs[0] ?? '').toLowerCase().split('-')[0];
  return FAMILY_FIRST.has(lang);
}

/** The cards with their names in the reader's order. Only a name Dugzi composed from given and family name is turned
 *  round; a name someone typed as a whole, or a company, stays as written. */
export function localNames<T extends Pick<Person, 'given' | 'family' | 'display'>>(people: T[], first = familyFirst()): T[] {
  if (!first) return people;
  return people.map((p) => (p.given && p.family && p.display === `${p.given} ${p.family}` ? { ...p, display: `${p.family} ${p.given}` } : p));
}

/** A card as the form it was saved from. */
export function formOf(p: Person): PersonForm {
  return { given: p.given, family: p.family, org: p.org, emails: p.emails, phones: p.phones, addresses: p.addresses, birthday: p.birthday, howWeMet: p.howWeMet, notes: p.notes, labels: p.labels };
}

const PARTICLES = new Set(['van', 'von', 'de', 'der', 'den', 'het', 'ter', 'ten', 'te', "'t", 'zu', 'la', 'le', 'du', 'des', 'del', 'della', 'da', 'di', 'dos', 'das', 'y']);

/** The letter a person is listed under (the same rule as the server's sort: family name without particles). */
export function letterOf(p: Pick<Person, 'family' | 'display'>): string {
  const words = p.family.split(/\s+/).filter(Boolean);
  const core = words.slice(words.findIndex((w) => !PARTICLES.has(w.toLowerCase())) >>> 0).join(' ');
  const base = (words.length && core ? core : p.display).trim();
  const c = base.normalize('NFD').replace(/[̀-ͯ]/g, '').charAt(0).toUpperCase();
  return /[A-Z]/.test(c) ? c : '#';
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export { MONTHS };

/** "14 March" or "14 March 1985". */
export function birthdayText(b: string): string {
  const m = /^(?:(\d{4})|-)-(\d{2})-(\d{2})$/.exec(b);
  if (!m) return b;
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}${m[1] ? ` ${m[1]}` : ''}`;
}

/** "12 March" or "12 March 2025" when it wasn't this year. */
export function savedText(iso: string): string {
  const d = new Date(iso);
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('en-GB', opts);
}

/** "today", "yesterday", "3 days ago", "2 weeks ago". */
export function agoText(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.round(days / 7)} weeks ago`;
  return `on ${savedText(iso)}`;
}

/** What a screen reader hears for a row: "Ana Souza, Mia's teacher, 2 emails". */
export function rowLabel(p: Person): string {
  const parts = [p.display];
  const who = p.howWeMet || p.org;
  if (who) parts.push(who);
  if (p.emails.length) parts.push(`${p.emails.length} ${p.emails.length === 1 ? 'email' : 'emails'}`);
  if (p.phones.length) parts.push(`${p.phones.length} ${p.phones.length === 1 ? 'phone' : 'phones'}`);
  return parts.join(', ');
}

/** A phone number for people: +31 6 12345678 stays as stored, but spaced after the country code. */
export function phoneText(v: string): string {
  const m = /^\+31(6)(\d{8})$/.exec(v);
  if (m) return `+31 6 ${m[2]}`;
  return v;
}
